/**
 * Resources — reference & emergency center.
 *
 * - Anaphylaxis response steps (embedded locally so this life-safety info shows
 *   even with no connectivity — mirrors backend emergencyController).
 * - The user's personalized emergency plan from GET /api/emergency (epinephrine
 *   status, medical-alert ID); degrades to their profile allergens if offline.
 * - Allergen reference library: the FDA Big-9 (+mustard) with hidden names, the
 *   user's own allergens highlighted.
 * - Trusted organizations + disclaimer.
 */

import React, { useState, useEffect } from 'react';
import { BookOpen, ExternalLink, Siren, ChevronDown, ShieldAlert, Syringe } from 'lucide-react';
import Card from '../common/Card';
import Badge from '../common/Badge';
import useAuth from '../../hooks/useAuth';
import useAPI from '../../hooks/useAPI';

// Mirrors backend emergencyController.ANAPHYLAXIS_STEPS — kept local so the steps
// always render. General first-aid information, not medical advice.
const ANAPHYLAXIS_STEPS = [
  'Use epinephrine auto-injector (EpiPen) immediately if available.',
  'Call emergency services (911) right away.',
  'Lay the person flat and raise their legs; if breathing is hard, let them sit up.',
  'If symptoms persist, a second dose may be given after 5–15 minutes.',
  'Go to the ER even if symptoms improve — reactions can return.',
];

// How to use an epinephrine auto-injector. General first-aid information based on
// standard EpiPen guidance — auto-injectors differ, so follow your device's label.
const EPIPEN_STEPS = [
  'Pull the auto-injector straight out of its carrier tube.',
  'Form a fist around it with the orange tip pointing down. Do not put your thumb, fingers, or hand over either end.',
  'Pull off the blue safety release straight up — don’t bend or twist it.',
  'Swing and push the orange tip firmly against the middle of the outer thigh until it clicks. It works through clothing.',
  'Hold firmly in place for 3 seconds.',
  'Remove and massage the injection site for about 10 seconds.',
  'Call 911, note the time of the dose, and get to an ER. A second dose may be needed after 5–15 minutes if there’s no improvement.',
];

// Mirrors backend ALLERGEN_MAP (utils/allergenChecker.js). Keep in sync.
const ALLERGEN_REFERENCE = {
  1: { name: 'Milk', hidden_names: ['casein', 'whey', 'lactose', 'cream', 'butter', 'cheese'] },
  2: { name: 'Eggs', hidden_names: ['albumin', 'globulin', 'lysozyme', 'mayonnaise', 'meringue'] },
  3: { name: 'Peanuts', hidden_names: ['arachis oil', 'groundnut'] },
  4: { name: 'Tree Nuts', hidden_names: ['almond', 'walnut', 'cashew', 'pistachio', 'pecan'] },
  5: { name: 'Shellfish', hidden_names: ['crustacean', 'shrimp', 'crab', 'lobster'] },
  6: { name: 'Fish', hidden_names: ['cod', 'salmon', 'tuna', 'anchovy'] },
  7: { name: 'Wheat', hidden_names: ['gluten', 'semolina', 'bulgur'] },
  8: { name: 'Soy', hidden_names: ['soybean', 'soya', 'shoyu'] },
  9: { name: 'Sesame', hidden_names: ['tahini', 'hummus', 'halvah'] },
  10: { name: 'Mustard', hidden_names: ['mustard seed', 'mustard powder'] },
};

const LINKS = [
  { name: 'FARE — Food Allergy Research & Education', url: 'https://www.foodallergy.org/' },
  { name: 'FDA — Food Allergies', url: 'https://www.fda.gov/food/food-labeling-nutrition/food-allergies' },
  { name: 'AAAAI — Find an Allergist', url: 'https://www.aaaai.org/' },
  { name: 'Kids With Food Allergies', url: 'https://www.kidswithfoodallergies.org/' },
  { name: 'AllergyEats — Allergy-Friendly Restaurants', url: 'https://www.allergyeats.com/' },
];

const EPI_BADGE = {
  valid: { variant: 'success', label: 'Valid' },
  expiring_soon: { variant: 'warning', label: 'Expiring soon' },
  expired: { variant: 'danger', label: 'Expired' },
};

export default function Resources() {
  const { user } = useAuth();
  const { get } = useAPI();
  const [plan, setPlan] = useState(null);
  const [openId, setOpenId] = useState(null);

  // Personalized plan; failure is non-fatal — we fall back to the profile.
  useEffect(() => {
    let active = true;
    get('/api/emergency')
      .then((data) => active && setPlan(data))
      .catch(() => active && setPlan(null));
    return () => {
      active = false;
    };
  }, [get]);

  const myAllergenIds =
    plan?.known_allergens?.map((a) => a.id) ||
    (Array.isArray(user?.allergies) ? user.allergies : []);
  const mySet = new Set(myAllergenIds);
  const epi = plan?.epinephrine?.status && EPI_BADGE[plan.epinephrine.status];

  return (
    <div className="page-container">
      <h1 className="page-title">Resources</h1>

      {/* Emergency action plan — always visible */}
      <Card className="emergency-card">
        <div className="emergency-header">
          <Siren size={22} className="icon-danger" aria-hidden="true" />
          <h2 className="emergency-title">In an Emergency</h2>
        </div>
        <p className="emergency-lead">
          Signs of a severe reaction (anaphylaxis): trouble breathing, swelling of
          lips/throat, hives, dizziness, or vomiting. Act fast:
        </p>
        <ol className="emergency-steps">
          {ANAPHYLAXIS_STEPS.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>

        <div className="emergency-meta">
          {epi && (
            <span className="emergency-meta-item">
              EpiPen: <Badge variant={epi.variant}>{epi.label}</Badge>
            </span>
          )}
          {plan?.user?.medical_alert_id && (
            <span className="emergency-meta-item">
              Medical alert ID: <strong>{plan.user.medical_alert_id}</strong>
            </span>
          )}
        </div>
      </Card>

      {/* How to use an EpiPen */}
      <Card className="resources-card epipen-card">
        <div className="resources-header">
          <Syringe size={20} className="icon-danger" />
          <h3 className="card-title">How to Use an EpiPen</h3>
        </div>
        <p className="epipen-mnemonic">“Blue to the sky, orange to the thigh.”</p>
        <ol className="emergency-steps">
          {EPIPEN_STEPS.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
        <p className="resources-sub epipen-note">
          Auto-injectors vary (EpiPen, Auvi-Q, generics) — always follow the
          instructions printed on your own device.
        </p>
      </Card>

      {/* Allergen reference library */}
      <Card className="resources-card">
        <div className="resources-header">
          <ShieldAlert size={20} />
          <h3 className="card-title">Allergen Reference</h3>
        </div>
        <p className="resources-sub">
          The major allergens and the hidden names they hide behind on labels.
          {myAllergenIds.length > 0 && ' Yours are marked.'}
        </p>
        <ul className="allergen-ref-list">
          {Object.entries(ALLERGEN_REFERENCE).map(([id, a]) => {
            const numId = Number(id);
            const mine = mySet.has(numId);
            const isOpen = openId === numId;
            return (
              <li key={id} className={`allergen-ref-item${mine ? ' is-mine' : ''}`}>
                <button
                  className="allergen-ref-toggle"
                  onClick={() => setOpenId(isOpen ? null : numId)}
                  aria-expanded={isOpen}
                >
                  <span className="allergen-ref-name">
                    {a.name}
                    {mine && <Badge variant="danger">Yours</Badge>}
                  </span>
                  <ChevronDown
                    size={18}
                    className={`allergen-ref-chevron${isOpen ? ' open' : ''}`}
                  />
                </button>
                {isOpen && (
                  <div className="allergen-ref-body">
                    <span className="allergen-ref-label">Also listed as:</span>{' '}
                    {a.hidden_names.join(', ')}.
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </Card>

      {/* Trusted organizations */}
      <Card className="resources-card">
        <div className="resources-header">
          <BookOpen size={20} />
          <h3 className="card-title">Trusted Organizations</h3>
        </div>
        <ul className="resource-list">
          {LINKS.map((link) => (
            <li key={link.url}>
              <a href={link.url} target="_blank" rel="noopener noreferrer" className="resource-link">
                <span>{link.name}</span>
                <ExternalLink size={16} />
              </a>
            </li>
          ))}
        </ul>
      </Card>

      <Card className="tips-card">
        <p className="auth-disclaimer">
          ⚠️ SafePlate is educational only and is not a substitute for medical
          advice. The emergency steps are general first-aid information. Always read
          product labels and consult your allergist.
        </p>
      </Card>
    </div>
  );
}
